// WizardShell — Dynamic wizard rendering shell that accepts a computed path.
// MANUS-WIZARD-V2 Task 3. Renders only the steps in the path, with a dynamic progress bar.
import { useCallback, useEffect } from 'react';
import type { WizardStep } from '../../lib/path-matrix';
import { getStepMeta } from './StepRegistry';

export type StepStatus = 'pending' | 'active' | 'complete' | 'failed' | 'skipped';

interface WizardShellProps {
  /** The computed wizard path — only these steps are rendered. */
  path: WizardStep[];
  /** Current step index within the path (0-based). */
  currentStepIndex: number;
  /** Status of each step in the path. */
  stepStatuses: Record<string, StepStatus>;
  /** Callback to render the current step's component. */
  renderStep: (stepId: WizardStep, stepIndex: number) => React.ReactNode;
  /** Navigate to a specific step index. */
  onNavigate: (stepIndex: number) => void;
  /** Error message to display. */
  error: string | null;
  /** Clear the error. */
  onClearError: () => void;
  /** Right panel content (capabilities). */
  rightPanel?: React.ReactNode;
}

export default function WizardShell({
  path,
  currentStepIndex,
  stepStatuses,
  renderStep,
  onNavigate,
  error,
  onClearError,
  rightPanel,
}: WizardShellProps) {
  const currentStep = path[currentStepIndex];
  const currentMeta = currentStep ? getStepMeta(currentStep) : null;
  const canGoBack = currentStepIndex > 0;

  // Keyboard: Enter advances if current step is complete
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter' && currentStep && stepStatuses[currentStep] === 'complete') {
      const next = currentStepIndex + 1;
      if (next < path.length) {
        onNavigate(next);
      }
    }
  }, [currentStep, currentStepIndex, path.length, stepStatuses, onNavigate]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="wizard-container">
      {/* Left: Active Step (60%) */}
      <div className="wizard-main">
        {/* Dynamic Progress Bar */}
        <div style={{ marginBottom: 24 }}>
          {/* Step counter */}
          <div style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text-secondary)',
            marginBottom: 8,
          }}>
            Step {currentStepIndex + 1} of {path.length}{currentMeta ? `: ${currentMeta.label}` : ''}
          </div>
          {/* Progress dots */}
          <div className="wizard-progress">
            {path.map((stepId, i) => {
              const meta = getStepMeta(stepId);
              const status = stepStatuses[stepId] || 'pending';
              const isCurrent = i === currentStepIndex;
              const canClick = status === 'complete' || status === 'failed';
              return (
                <div
                  key={stepId}
                  className={`wizard-progress-item ${status} ${isCurrent ? 'current' : ''}`}
                  onClick={() => { if (canClick) onNavigate(i); }}
                  style={{ cursor: canClick ? 'pointer' : 'default' }}
                >
                  <div className="wizard-progress-dot">
                    {status === 'complete' ? '✓' : status === 'failed' ? '✗' : status === 'skipped' ? '–' : i + 1}
                  </div>
                  <span className="wizard-progress-label">{meta.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="wizard-error-box" style={{ margin: '0 0 16px' }}>
            {error}
            <button onClick={onClearError} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
          </div>
        )}

        {/* Active Step Content */}
        {currentStep && renderStep(currentStep, currentStepIndex)}

        {/* Back button */}
        {canGoBack && (
          <div style={{ marginTop: 16 }}>
            <button
              onClick={() => onNavigate(currentStepIndex - 1)}
              style={{
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 500,
                border: '1px solid var(--border)',
                borderRadius: 8,
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              ← Back
            </button>
          </div>
        )}
      </div>

      {/* Right: Panel (40%) */}
      {rightPanel && (
        <div className="wizard-panel">
          {rightPanel}
        </div>
      )}
    </div>
  );
}
